import { authFetch } from "./authFetch";
import { BACKEND_URL } from "lib/utils/baseUrl";

const toYmd = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (!(value instanceof Date)) return "";
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const parseJson = async (response) => {
  try {
    return await response.json();
  } catch {
    return null;
  }
};

export function getAdminFileDownloadUrl(fileId) {
  if (!fileId) return "";
  return `${BACKEND_URL}/admin/files/${encodeURIComponent(fileId)}/download`;
}

export async function getAdminFilesList(query = {}) {
  const {
    keyword = "",
    searchField = "all",
    statusFilter = "all",
    typeFilter = "all",
    facilityFilter = "all",
    startDate = null,
    endDate = null,
    sortKey = "registeredAt",
    sortDir = "DESC",
    page = 1,
    limit = 20,
  } = query;

  const qs = new URLSearchParams();
  qs.set("searchField", searchField || "all");
  qs.set("statusFilter", statusFilter || "all");
  qs.set("typeFilter", typeFilter || "all");
  qs.set("facilityFilter", facilityFilter || "all");
  qs.set("sortKey", sortKey || "registeredAt");
  qs.set("sortDir", String(sortDir).toUpperCase() === "ASC" ? "ASC" : "DESC");
  qs.set("page", String(Math.max(1, Number(page) || 1)));
  qs.set("limit", String(Math.max(10, Number(limit) || 20)));

  const safeKeyword = String(keyword ?? "").trim();
  if (safeKeyword) qs.set("keyword", safeKeyword);

  const startYmd = toYmd(startDate);
  const endYmd = toYmd(endDate);
  if (startYmd) qs.set("startDate", startYmd);
  if (endYmd) qs.set("endDate", endYmd);

  const response = await authFetch(
    `${BACKEND_URL}/admin/files?${qs.toString()}`,
    { method: "GET" },
    { refreshUrlIncludes: "auth/refresh" },
  );
  const result = await parseJson(response);

  if (!response.ok || result?.success !== true) {
    return {
      success: false,
      message: result?.message || "파일 목록 조회 실패",
      rows: [],
      totalCount: 0,
      pagination: {
        currentPage: Number(page) || 1,
        totalPages: 1,
        limit: Number(limit) || 20,
      },
    };
  }

  const data = result?.data ?? {};

  return {
    success: true,
    message: result?.message || "파일 목록을 불러왔습니다.",
    rows: Array.isArray(data.rows) ? data.rows : [],
    totalCount: Number(data.totalCount ?? 0),
    pagination: {
      currentPage: Number(data.pagination?.currentPage ?? page),
      totalPages: Number(data.pagination?.totalPages ?? 1),
      limit: Number(data.pagination?.limit ?? limit),
    },
    sort: data.sort ?? { sortKey, sortDir },
  };
}

export async function getAdminFileDetail(fileId, { locale = "" } = {}) {
  if (!fileId) {
    return {
      success: false,
      data: null,
      message: "파일 ID가 없습니다.",
    };
  }

  const qs = new URLSearchParams();
  if (locale) qs.set("locale", locale);

  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  const response = await authFetch(
    `${BACKEND_URL}/admin/files/${encodeURIComponent(fileId)}${suffix}`,
    { method: "GET" },
    { refreshUrlIncludes: "auth/refresh" },
  );
  const result = await parseJson(response);

  if (!response.ok || result?.success !== true) {
    return {
      success: false,
      data: null,
      message: result?.message || "파일을 불러올 수 없습니다.",
    };
  }

  return {
    success: true,
    message: result?.message || "파일 정보를 불러왔습니다.",
    data: {
      ...(result.data ?? {}),
      adminOriginalDownloadUrl: getAdminFileDownloadUrl(
        result.data?.upload_id || fileId,
      ),
    },
    next_file: result.next_file ?? null,
    prev_file: result.prev_file ?? null,
  };
}
